import { useMemo, useRef, useState } from "react"
import {
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"

import { NO_PRICE_LIST_ID } from "../priceListApi"
import type { NoPriceListId, PriceListType } from "../priceListApi"

type PriceListOption = PriceListType | Readonly<{ id: NoPriceListId; name: string; isActive: true }>

type PriceListDropdownSelectorProps = Readonly<{
    priceLists: ReadonlyArray<PriceListType>
    isLoading: boolean
    selectedPriceListId: number | NoPriceListId | null
    onPriceListChange: (id: number | NoPriceListId | null) => void
    showNoPriceListOption?: boolean
}>

const TRIGGER_WIDTH = "28rem"
const MENU_WIDTH = "40rem"

const useStyles = makeStyles()((theme) => ({
    paper: {
        display: "flex",
        alignItems: "center",
        width: TRIGGER_WIDTH,
        padding: ".125rem .25rem",
        borderRadius: ".5rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        boxShadow: "none",
        cursor: "pointer",
    },
    triggerContent: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: ".25rem .5rem",
    },
    fieldLabel: {
        fontSize: ".6875rem",
        lineHeight: 1.2,
        color: theme.palette.tailwind.slate[500],
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    triggerValue: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: ".875rem",
        lineHeight: 1.3,
        color: theme.palette.tailwind.slate[900],
    },
    triggerPlaceholder: {
        color: theme.palette.tailwind.slate[400],
    },
    chevronButton: {
        padding: ".5rem",
        color: theme.palette.tailwind.slate[500],
        fontSize: ".875rem",
    },
    chevronIcon: {
        transition: "transform 0.2s ease-in-out",
    },
    chevronIconOpen: {
        transform: "rotate(180deg)",
    },
    divider: {
        height: "1.75rem",
        margin: ".25rem .5rem",
    },
    checkboxLabel: {
        marginRight: ".25rem",
        "& .MuiFormControlLabel-label": {
            fontSize: ".8125rem",
            whiteSpace: "nowrap",
        },
    },
    menuPaper: {
        minWidth: MENU_WIDTH,
        maxWidth: MENU_WIDTH,
    },
    menuItem: {
        padding: ".5rem 1rem",
    },
    optionRow: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        width: "100%",
        gap: ".5rem",
    },
    optionLeft: {
        display: "flex",
        flexDirection: "column",
        gap: ".125rem",
        minWidth: 0,
        flex: 1,
    },
    optionNameRow: {
        display: "flex",
        alignItems: "start",
        gap: ".5rem",
        minWidth: 0,
        width: "fit-content",
    },
    optionName: {
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        minWidth: 0,
        flex: 1,
    },
    optionMetaText: {
        color: theme.palette.tailwind.slate[500],
        fontSize: ".75rem",
        lineHeight: 1.3,
    },
    inactiveChip: {
        height: "1.25rem",
        fontSize: ".6875rem",
    },
    infoIcon: {
        color: theme.palette.tailwind.slate[400],
        fontSize: ".875rem",
        flexShrink: 0,
    },
    tooltipTitle: {
        fontWeight: 600,
        marginBottom: ".125rem",
    },
    tooltipSection: {
        "&:not(:last-child)": {
            marginBottom: ".375rem",
        },
    },
}))

export function PriceListDropdownSelector({
    priceLists,
    isLoading,
    selectedPriceListId,
    onPriceListChange,
    showNoPriceListOption = false,
}: PriceListDropdownSelectorProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const [showActiveOnly, setShowActiveOnly] = useState(true)
    const [isOpen, setIsOpen] = useState(false)
    const anchorRef = useRef<HTMLDivElement | null>(null)

    const options = useMemo((): ReadonlyArray<PriceListOption> => {
        const filtered = showActiveOnly ? priceLists.filter((pl) => pl.isActive) : [...priceLists]
        const noPriceListOption: PriceListOption = {
            id: NO_PRICE_LIST_ID,
            name: l10n.getString("products-no-price-list-tab"),
            isActive: true,
        }
        return showNoPriceListOption ? [noPriceListOption, ...filtered] : filtered
    }, [priceLists, showActiveOnly, showNoPriceListOption, l10n])

    const selectedOption = useMemo(
        () => options.find((opt) => opt.id === selectedPriceListId) ?? null,
        [options, selectedPriceListId],
    )

    const handleSelect = (id: number | NoPriceListId): void => {
        onPriceListChange(id)
        setIsOpen(false)
    }

    const buildTooltipContent = (priceList: PriceListType): React.ReactNode => {
        const hasInternalNote = priceList.internalNote?.trim()
        const hasMessageToCustomers = priceList.messageToCustomers?.trim()

        if (!hasInternalNote && !hasMessageToCustomers) {
            return (
                <LocalizedStrict id="price-list-dropdown-selector-no-info">
                    <Typography variant="body2">No additional info</Typography>
                </LocalizedStrict>
            )
        }

        return (
            <>
                {hasInternalNote && (
                    <div className={classes.tooltipSection}>
                        <LocalizedStrict id="price-list-dropdown-selector-internal-note-label">
                            <Typography variant="body2" className={classes.tooltipTitle}>
                                Internal note
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="body2">{priceList.internalNote}</Typography>
                    </div>
                )}
                {hasMessageToCustomers && (
                    <div className={classes.tooltipSection}>
                        <LocalizedStrict id="price-list-dropdown-selector-message-to-customers-label">
                            <Typography variant="body2" className={classes.tooltipTitle}>
                                Message to customers
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="body2">{priceList.messageToCustomers}</Typography>
                    </div>
                )}
            </>
        )
    }

    const isPlaceholder = !selectedOption

    // Upcoming feature: when API returns `upcomingActivationDate`, render an "Activates on <date>"
    // row here for pricelists that are inactive but scheduled to become active in the future.

    const renderVatRow = (priceList: PriceListType, textClass: string): React.ReactNode => {
        if (priceList.includeVat === undefined) return null
        return priceList.includeVat ? (
            <LocalizedStrict id="price-list-dropdown-selector-vat-inclusive">
                <Typography className={textClass}>Price incl. VAT</Typography>
            </LocalizedStrict>
        ) : (
            <LocalizedStrict id="price-list-dropdown-selector-vat-exclusive">
                <Typography className={textClass}>Price excl. VAT</Typography>
            </LocalizedStrict>
        )
    }

    return (
        <>
            <Paper
                ref={anchorRef}
                className={classes.paper}
                onClick={() => {
                    if (!isLoading) setIsOpen((prev) => !prev)
                }}
            >
                <IconButton
                    size="small"
                    className={classes.chevronButton}
                    disabled={isLoading}
                    onClick={(e) => {
                        e.stopPropagation()
                        setIsOpen((prev) => !prev)
                    }}
                    aria-label="toggle dropdown"
                >
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={cx(classes.chevronIcon, isOpen && classes.chevronIconOpen)}
                    />
                </IconButton>
                <div className={classes.triggerContent}>
                    <LocalizedStrict id="price-list-dropdown-selector-field-label">
                        <Typography className={classes.fieldLabel}>Price list</Typography>
                    </LocalizedStrict>
                    <Tooltip title={selectedOption?.name ?? ""} disableHoverListener={isPlaceholder} placement="top">
                        <Typography className={cx(classes.triggerValue, isPlaceholder && classes.triggerPlaceholder)}>
                            {selectedOption ? (
                                selectedOption.name
                            ) : (
                                <LocalizedStrict id="price-list-dropdown-selector-placeholder">
                                    <span>Select price list</span>
                                </LocalizedStrict>
                            )}
                        </Typography>
                    </Tooltip>
                </div>
                <Divider orientation="vertical" className={classes.divider} />
                <LocalizedStrict id="price-list-dropdown-selector-active-only-checkbox" attrs={{ label: true }}>
                    <FormControlLabel
                        className={classes.checkboxLabel}
                        control={
                            <Checkbox
                                checked={showActiveOnly}
                                onChange={(_, checked) => setShowActiveOnly(checked)}
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                            />
                        }
                        label=""
                        onClick={(e) => e.stopPropagation()}
                    />
                </LocalizedStrict>
            </Paper>

            <Menu
                anchorEl={anchorRef.current}
                open={isOpen}
                onClose={() => setIsOpen(false)}
                slotProps={{ paper: { className: classes.menuPaper } }}
            >
                {options.map((option) => {
                    const isPriceListOption = typeof option.id === "number"
                    return (
                        <MenuItem
                            key={option.id}
                            selected={option.id === selectedPriceListId}
                            className={classes.menuItem}
                            onClick={() => handleSelect(option.id)}
                        >
                            <div className={classes.optionRow}>
                                <div className={classes.optionLeft}>
                                    <div className={classes.optionNameRow}>
                                        <Tooltip title={option.name} placement="top">
                                            <Typography variant="body2" className={classes.optionName}>
                                                {option.name}
                                            </Typography>
                                        </Tooltip>
                                        {isPriceListOption && !option.isActive && (
                                            <LocalizedStrict
                                                id="price-list-dropdown-selector-inactive-chip"
                                                attrs={{ label: true }}
                                            >
                                                <Chip label="" size="small" className={classes.inactiveChip} />
                                            </LocalizedStrict>
                                        )}
                                    </div>
                                    {isPriceListOption && renderVatRow(option, classes.optionMetaText)}
                                </div>
                                {isPriceListOption && (
                                    <Tooltip title={buildTooltipContent(option)} arrow placement="right">
                                        <span onClick={(e) => e.stopPropagation()}>
                                            <FontAwesomeIcon icon={faInfoCircle} className={classes.infoIcon} />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </MenuItem>
                    )
                })}
            </Menu>
        </>
    )
}
